import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Empty,
  InputNumber,
  List,
  Modal,
  Select,
  Switch,
  Tooltip,
} from "antd";
import { BellRinging, Trash } from "@phosphor-icons/react";
import {
  fetchReviewReminders,
  createReviewReminder,
  updateReviewReminder,
  deleteReviewReminder,
} from "../../../slice/HomeSlice";

const ANYONE = "";

const channelLabel = (rule) => {
  if (rule.send_email && rule.send_in_app) return "Email + in-app";
  if (rule.send_email) return "Email";
  return "In-app";
};

const scopeLabel = (rule) =>
  rule.assigned_by_details
    ? `assigned by ${rule.assigned_by_details.full_name}`
    : "from anyone";

const daysLabel = (days) => {
  if (days === 0) return "on the due date";
  if (days === 1) return "1 day before due";
  return `${days} days before due`;
};

export const ruleDescription = (rule) =>
  `Remind me ${daysLabel(rule.days_before)} — reviews ${scopeLabel(rule)} (${channelLabel(rule)})`;

const RemindersButton = () => {
  const dispatch = useDispatch();
  const { rules, assignerOptions, isLoading } = useSelector(
    (state) => state.home.reviewReminders
  );

  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [daysBefore, setDaysBefore] = useState(3);
  const [assignedBy, setAssignedBy] = useState(ANYONE);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendInApp, setSendInApp] = useState(true);

  useEffect(() => {
    if (open) {
      setError(null);
      dispatch(fetchReviewReminders());
    }
  }, [open, dispatch]);

  const handleAdd = async () => {
    setError(null);
    setSaving(true);
    const result = await dispatch(
      createReviewReminder({
        days_before: daysBefore ?? 3,
        assigned_by: assignedBy === ANYONE ? null : assignedBy,
        send_email: sendEmail,
        send_in_app: sendInApp,
      })
    );
    setSaving(false);
    if (createReviewReminder.rejected.match(result)) {
      setError(result.payload);
      return;
    }
    setDaysBefore(3);
    setAssignedBy(ANYONE);
    setSendEmail(true);
    setSendInApp(true);
  };

  const handleToggle = async (rule, isActive) => {
    setError(null);
    const result = await dispatch(
      updateReviewReminder({ id: rule.id, is_active: isActive })
    );
    if (updateReviewReminder.rejected.match(result)) {
      setError(result.payload);
    }
  };

  const handleDelete = async (rule) => {
    setError(null);
    const result = await dispatch(deleteReviewReminder(rule.id));
    if (deleteReviewReminder.rejected.match(result)) {
      setError(result.payload);
    }
  };

  const assignerSelectOptions = [
    { value: ANYONE, label: "Anyone" },
    ...assignerOptions.map((user) => ({
      value: user.id,
      label: user.full_name,
    })),
  ];

  return (
    <>
      <Button
        icon={<BellRinging size={16} style={{ verticalAlign: "-3px" }} />}
        onClick={() => setOpen(true)}
        data-testid="review-reminders-button"
      >
        Reminders
      </Button>
      <Modal
        title="Review reminders"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <p style={{ color: "#666", marginTop: 0 }}>
          Get an email or an in-app notification before items under
          &ldquo;Needs my review&rdquo; are due.
        </p>

        {error ? (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <List
          loading={isLoading}
          dataSource={rules}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No reminders yet"
              />
            ),
          }}
          renderItem={(rule) => (
            <List.Item
              key={rule.id}
              actions={[
                <Tooltip
                  title={rule.is_active ? "Pause reminder" : "Resume reminder"}
                  key="toggle"
                >
                  <Switch
                    size="small"
                    checked={rule.is_active}
                    onChange={(checked) => handleToggle(rule, checked)}
                    aria-label="Toggle reminder"
                  />
                </Tooltip>,
                <Tooltip title="Delete reminder" key="delete">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<Trash size={16} />}
                    onClick={() => handleDelete(rule)}
                    aria-label="Delete reminder"
                  />
                </Tooltip>,
              ]}
            >
              <span style={{ opacity: rule.is_active ? 1 : 0.5 }}>
                {ruleDescription(rule)}
              </span>
            </List.Item>
          )}
        />

        <Divider style={{ margin: "12px 0" }}>Add a reminder</Divider>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span>Remind me</span>
          <InputNumber
            min={0}
            max={90}
            value={daysBefore}
            onChange={setDaysBefore}
            style={{ width: 64 }}
            aria-label="Days before due"
          />
          <span>day(s) before reviews assigned by</span>
          <Select
            value={assignedBy}
            onChange={setAssignedBy}
            options={assignerSelectOptions}
            style={{ minWidth: 160 }}
            showSearch
            optionFilterProp="label"
            aria-label="Assigned by"
          />
          <span>are due</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 16,
          }}
        >
          <div style={{ display: "flex", gap: 16 }}>
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            >
              Email
            </Checkbox>
            <Checkbox
              checked={sendInApp}
              onChange={(e) => setSendInApp(e.target.checked)}
            >
              In-app notification
            </Checkbox>
          </div>
          <Button
            type="primary"
            onClick={handleAdd}
            loading={saving}
            disabled={(!sendEmail && !sendInApp) || daysBefore === null}
          >
            Add reminder
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default RemindersButton;
